/**
 * Einen Entwurf aus der KIAN-Karte bearbeiten. Gewählte Werte gehen als ID in den Entwurf
 * (`statusId`, `customerId` …) — der ursprüngliche Text bleibt als Herkunft stehen, die ID gewinnt.
 * Gespeichert wird über PATCH /kian/actions/:id; der Server löst danach alles neu auf.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Button,
  Checkbox,
  Divider,
  Group,
  Modal,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import { useCustomerSearchSelect } from './customerSearchSelect';
import { KianDraftContactField } from './KianDraftContactField';
import { isResumeOnly, kianAssigneeOptions } from './kianActionUi';
import type {
  KianActionItem,
  KianActionKind,
  KianDraftComment,
  KianTicketDraft,
} from '../../../lib/api/domains/kianActions';

interface Props {
  opened: boolean;
  onClose: () => void;
  kind: KianActionKind;
  item: KianActionItem | null;
  saving: boolean;
  onSave: (draft: KianTicketDraft) => void;
}

/** Leere Auswahl = „wie im Text/automatisch" */
const AUTO = '__auto__';

export function KianDraftEditorModal({ opened, onClose, kind, item, saving, onSave }: Props) {
  const create = kind === 'TICKET_CREATE';
  const [draft, setDraft] = useState<KianTicketDraft>({});
  const resolvedCustomer = item?.resolved?.customer ?? null;
  const selectedCustomerId = draft.customerId ?? resolvedCustomer?.id ?? null;
  const customerSelect = useCustomerSearchSelect({
    enabled: opened,
    selectedId: selectedCustomerId,
    fallbackLabel:
      resolvedCustomer && resolvedCustomer.id === selectedCustomerId ? resolvedCustomer.name : (draft.customer ?? null),
  });

  useEffect(() => {
    if (opened && item) {
      setDraft({ ...(item.draft ?? {}) });
      customerSelect.reset();
    }
  }, [opened, item]);

  const metadata = useQuery({
    queryKey: ['weclapp-metadata'],
    queryFn: () => api.getWeclappMetadata(),
    staleTime: 5 * 60_000,
    enabled: opened,
  });
  const users = useQuery({
    queryKey: ['portal-technicians'],
    queryFn: () => api.getPortalTechnicians(),
    staleTime: 120_000,
    enabled: opened,
  });

  const set = <K extends keyof KianTicketDraft>(k: K, v: KianTicketDraft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const resolved = item?.resolved ?? null;

  /**
   * Beschriftung der Option „automatisch": was der Entwurf sagt — oder, wenn er nichts sagt, was
   * der Server daraus gemacht hat (z. B. Warte-Status wegen Blocker, du als Bearbeiter wegen Status).
   */
  const autoLabel = (text: string | null | undefined, resolvedName: string | null | undefined, fallback: string) =>
    text ? `Wie angegeben: „${text}"${resolvedName ? ` → ${resolvedName}` : ''}` : resolvedName ? `Automatisch: ${resolvedName}` : fallback;

  const statusOptions = useMemo(
    () => [
      { value: AUTO, label: autoLabel(draft.status, resolved?.status?.name, create ? 'Neu (Standard)' : 'Nicht ändern') },
      ...(metadata.data?.statuses ?? []).map((s) => ({ value: String(s.id), label: s.name })),
    ],
    [metadata.data, draft.status, create, resolved],
  );
  const priorityOptions = useMemo(
    () => [
      { value: AUTO, label: autoLabel(draft.priority, resolved?.priority?.name, create ? 'Standard' : 'Nicht ändern') },
      ...(metadata.data?.priorities ?? []).map((p) => ({ value: String(p.id), label: p.name })),
    ],
    [metadata.data, draft.priority, create, resolved],
  );
  const categoryOptions = useMemo(
    () => [
      { value: AUTO, label: autoLabel(draft.category, resolved?.category?.name, create ? 'Keine' : 'Nicht ändern') },
      ...(metadata.data?.categories ?? []).map((c) => ({ value: String(c.id), label: c.name })),
    ],
    [metadata.data, draft.category, create, resolved],
  );
  const userOptions = useMemo(
    () => [
      {
        value: AUTO,
        label: autoLabel(
          draft.assignee,
          resolved?.assignee ? (resolved.assignee.isSelf ? 'du' : resolved.assignee.name) : null,
          create ? 'Niemand (Pool)' : 'Nicht ändern',
        ),
      },
      { value: '__self__', label: 'Ich' },
      ...kianAssigneeOptions(users.data?.technicians),
    ],
    [users.data, draft.assignee, create, resolved],
  );
  const comments = draft.comments ?? [];
  const setComment = (i: number, patch: Partial<KianDraftComment>) =>
    set(
      'comments',
      comments.map((c, idx) => (idx === i ? { ...c, ...patch } : c)),
    );

  const assigneeValue = draft.assigneeId ?? (draft.assignee && /^(ich|mir|me|self)$/i.test(draft.assignee) ? '__self__' : AUTO);
  const resumeOnly = !!item && isResumeOnly(item, kind);
  const assigneeSelect = (
    <Select
      label="Bearbeiter"
      data={userOptions}
      value={assigneeValue}
      onChange={(v) => {
        if (!v || v === AUTO) setDraft((d) => ({ ...d, assigneeId: null }));
        else if (v === '__self__') setDraft((d) => ({ ...d, assigneeId: null, assignee: 'ich' }));
        else setDraft((d) => ({ ...d, assigneeId: v, unassign: null }));
      }}
      searchable
      disabled={!!draft.unassign}
      comboboxProps={{ zIndex: 5400 }}
    />
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={resumeOnly ? 'Offene Schritte korrigieren' : create ? 'Ticket-Entwurf bearbeiten' : 'Änderung bearbeiten'}
      size="lg"
      centered
      zIndex={5300}
    >
      {item && resumeOnly ? (
        <Stack gap="sm">
          <Alert color="blue" variant="light" py={6}>
            Ticket <b>{item.ticketNumber ?? item.ticketId}</b> ist schon angelegt, die Kommentare sind drin. Offen sind
            nur noch Status und Bearbeiter — alles andere steht bereits im Ticket.
          </Alert>
          {item.error ? (
            <Text size="xs" c="red">
              {item.error}
            </Text>
          ) : null}
          <Group grow align="flex-start">
            <Select
              label="Status"
              data={statusOptions}
              value={draft.statusId ?? AUTO}
              onChange={(v) => set('statusId', !v || v === AUTO ? null : v)}
              searchable
              comboboxProps={{ zIndex: 5400 }}
            />
            {assigneeSelect}
          </Group>
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose} disabled={saving}>
              Abbrechen
            </Button>
            <Button
              loading={saving}
              onClick={() =>
                // Nur die offenen Felder — der Rest geht unverändert zurück (Server prüft das)
                onSave({
                  ...(item.draft ?? {}),
                  statusId: draft.statusId ?? null,
                  status: draft.status ?? null,
                  assigneeId: draft.assigneeId ?? null,
                  assignee: draft.assignee ?? null,
                  assignReason: draft.assignReason ?? null,
                })
              }
            >
              Übernehmen
            </Button>
          </Group>
        </Stack>
      ) : item ? (
        <Stack gap="sm">
          {!create && resolved?.ticket ? (
            <Text size="sm">
              Ticket <b>{resolved.ticket.number ?? resolved.ticket.id}</b>
              {resolved.ticket.subject ? ` · ${resolved.ticket.subject}` : ''}
              {resolved.ticket.partyName ? ` · ${resolved.ticket.partyName}` : ''}
            </Text>
          ) : null}

          {create || draft.customerId || draft.customer ? (
            <Select
              label={create ? 'Kunde' : 'Kunde ändern auf'}
              placeholder="Kundennamen oder -nummer eingeben…"
              data={customerSelect.data}
              value={selectedCustomerId}
              onChange={(v) =>
                // Der Ansprechpartner gehört zum Kunden — anderer Kunde, Ansprechpartner neu wählen.
                setDraft((d) => (v === selectedCustomerId ? d : { ...d, customerId: v, contactId: null, contact: null }))
              }
              searchable
              searchValue={customerSelect.searchValue}
              onSearchChange={customerSelect.onSearchChange}
              clearable={!create}
              nothingFoundMessage={customerSelect.nothingFoundMessage}
              description={draft.customer && !draft.customerId ? `Aus dem Entwurf: „${draft.customer}"` : undefined}
              comboboxProps={{ zIndex: 5400 }}
            />
          ) : null}

          {create ? (
            <KianDraftContactField
              partyId={selectedCustomerId}
              draft={draft}
              resolvedContact={draft.customerId && draft.customerId !== resolvedCustomer?.id ? null : resolved?.contact}
              onChange={(next) => setDraft((d) => ({ ...d, ...next }))}
              zIndex={5300}
            />
          ) : null}

          <TextInput
            label={create ? 'Betreff' : 'Neuer Betreff (leer = unverändert)'}
            value={draft.subject ?? ''}
            onChange={(e) => {
              const v = e.currentTarget.value;
              set('subject', v || null);
            }}
            required={create}
            maxLength={500}
          />
          <Textarea
            label={create ? 'Beschreibung' : 'Neue Beschreibung (leer = unverändert, ersetzt die alte)'}
            value={draft.description ?? ''}
            onChange={(e) => {
              const v = e.currentTarget.value;
              set('description', v || null);
            }}
            autosize
            minRows={3}
            maxRows={12}
          />

          <Group grow align="flex-start">
            <Select
              label="Status"
              data={statusOptions}
              value={draft.statusId ?? AUTO}
              onChange={(v) => set('statusId', !v || v === AUTO ? null : v)}
              searchable
              comboboxProps={{ zIndex: 5400 }}
            />
            <Select
              label="Priorität"
              data={priorityOptions}
              value={draft.priorityId ?? AUTO}
              onChange={(v) => set('priorityId', !v || v === AUTO ? null : v)}
              comboboxProps={{ zIndex: 5400 }}
            />
          </Group>
          <Group grow align="flex-start">
            <Select
              label="Kategorie"
              data={categoryOptions}
              value={draft.categoryId ?? AUTO}
              onChange={(v) => set('categoryId', !v || v === AUTO ? null : v)}
              searchable
              comboboxProps={{ zIndex: 5400 }}
            />
            {assigneeSelect}
          </Group>
          {!create ? (
            <>
              <Checkbox
                label="Zurück in den Pool (Bearbeiter entfernen)"
                checked={!!draft.unassign}
                onChange={(e) => {
                  const on = e.currentTarget.checked;
                  setDraft((d) => ({ ...d, unassign: on || null, ...(on ? { assigneeId: null, assignee: null } : {}) }));
                }}
              />
              {draft.unassign || (draft.assigneeId && draft.assigneeId !== resolved?.ticket?.assigneeId) ? (
                <TextInput
                  label="Begründung"
                  description="Pflicht bei Pool-Rückgabe und bei Zuweisung an andere durch den Teamlead (mind. 10 Zeichen)"
                  value={draft.assignReason ?? ''}
                  onChange={(e) => {
                    const v = e.currentTarget.value;
                    set('assignReason', v || null);
                  }}
                />
              ) : null}
            </>
          ) : null}

          <TextInput
            label="Fällig am"
            type="date"
            value={draft.dueDate ?? ''}
            onChange={(e) => {
              const v = e.currentTarget.value;
              set('dueDate', v || null);
            }}
          />

          <Divider label="Blocker — wartet auf Dritte" labelPosition="left" />
          <Switch
            label="Ticket wartet auf Dritte (Lieferant, Provider, Hersteller, Kunde)"
            checked={!!draft.blocker}
            onChange={(e) => {
              const on = e.currentTarget.checked;
              setDraft((d) => ({ ...d, blocker: on ? { waitingFor: d.blocker?.waitingFor ?? '' } : null, ...(on ? { clearBlocker: null } : {}) }));
            }}
          />
          {draft.blocker ? (
            <Stack gap="xs" pl="md">
              <TextInput
                label="Worauf / auf wen wird gewartet?"
                placeholder="z. B. Telekom, Lieferant Bechtle, Rückmeldung Kunde"
                value={draft.blocker.waitingFor}
                onChange={(e) => {
                  const v = e.currentTarget.value;
                  setDraft((d) => ({ ...d, blocker: { ...(d.blocker ?? { waitingFor: '' }), waitingFor: v } }));
                }}
                required
              />
              <Group grow>
                <TextInput
                  label="Seit"
                  type="date"
                  value={draft.blocker.since ?? ''}
                  onChange={(e) => {
                    const v = e.currentTarget.value;
                    setDraft((d) => ({ ...d, blocker: { ...(d.blocker ?? { waitingFor: '' }), since: v || null } }));
                  }}
                />
                <TextInput
                  label="Nachfassen am"
                  type="date"
                  value={draft.blocker.followUp ?? ''}
                  onChange={(e) => {
                    const v = e.currentTarget.value;
                    setDraft((d) => ({ ...d, blocker: { ...(d.blocker ?? { waitingFor: '' }), followUp: v || null } }));
                  }}
                />
              </Group>
              <Textarea
                label="Notiz"
                value={draft.blocker.note ?? ''}
                autosize
                minRows={1}
                onChange={(e) => {
                  const v = e.currentTarget.value;
                  setDraft((d) => ({ ...d, blocker: { ...(d.blocker ?? { waitingFor: '' }), note: v || null } }));
                }}
              />
            </Stack>
          ) : !create ? (
            <Checkbox
              label="Warten beenden — Ticket wieder in Bearbeitung"
              checked={!!draft.clearBlocker}
              onChange={(e) => {
                const on = e.currentTarget.checked;
                set('clearBlocker', on || null);
              }}
            />
          ) : null}

          <Divider label={`Kommentare (${comments.length})`} labelPosition="left" />
          {comments.map((c, i) => (
            <Group key={i} align="flex-start" gap="xs" wrap="nowrap">
              <Textarea
                style={{ flex: 1 }}
                value={c.text}
                autosize
                minRows={1}
                maxRows={8}
                description={[c.date, c.author].filter(Boolean).join(' · ') || undefined}
                onChange={(e) => {
                  const v = e.currentTarget.value;
                  setComment(i, { text: v });
                }}
              />
              <Stack gap={4} align="center">
                <Switch
                  size="xs"
                  label="intern"
                  checked={c.internal !== false}
                  onChange={(e) => {
                    const on = e.currentTarget.checked;
                    setComment(i, { internal: on });
                  }}
                />
                <ActionIcon
                  variant="subtle"
                  color="red"
                  aria-label="Kommentar entfernen"
                  onClick={() => set('comments', comments.filter((_, idx) => idx !== i))}
                >
                  <IconTrash size={14} />
                </ActionIcon>
              </Stack>
            </Group>
          ))}
          <Group>
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={() => set('comments', [...comments, { text: '', internal: true }])}
            >
              Kommentar hinzufügen
            </Button>
          </Group>
          {comments.some((c) => c.internal === false) ? (
            <Alert color="yellow" variant="light" py={6}>
              Nicht interne Kommentare sieht der Kunde (Kundenportal, ggf. Mail von weclapp).
            </Alert>
          ) : null}

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose} disabled={saving}>
              Abbrechen
            </Button>
            <Button
              loading={saving}
              onClick={() =>
                onSave({
                  ...draft,
                  comments: (draft.comments ?? []).filter((c) => c.text.trim()),
                  blocker: draft.blocker && draft.blocker.waitingFor.trim() ? draft.blocker : null,
                })
              }
            >
              Übernehmen
            </Button>
          </Group>
        </Stack>
      ) : null}
    </Modal>
  );
}
